// MissionModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './MissionModal.css';

// Components
import MissionEditorForm, {
  MISSION_EDITOR_SECTION_ABOUT,
  MISSION_EDITOR_SECTION_TASKS,
} from './MissionEditorForm';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import Modal from 'components/modal/Modal';
import Tabs from 'components/tabs/Tabs';

/**
 * MissionModal
 *
 * Workflow settings: About + Tasks list. Graph editing lives in Design workflow.
 *
 * @returns {ReactNode} The rendered component.
 */
const MissionModal = React.memo(({
  canSaveMissionMeta = false,
  isOpen = false,
  isSaving = false,
  missionDescription = '',
  missionIconKey = 'account_tree',
  missionMetaError = '',
  missionTasks = [],
  missionTitle = '',
  mode = 'create',
  onAddMissionTask,
  onDeleteMissionTemplate,
  onDesignWorkflow,
  onEditMissionTask,
  onMissionDescriptionChange,
  onMissionIconKeyChange,
  onMissionTitleChange,
  onReorderMissionTask,
  onSaveMissionMeta,
  selectedMissionTemplateKey = '',
  setOpen,
}) => {
  const isMobile = useIsMobile();
  const [selectedTab, setSelectedTab] = useState(MISSION_EDITOR_SECTION_ABOUT);
  const canDesign = mode === 'edit' && Boolean(selectedMissionTemplateKey);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTab(MISSION_EDITOR_SECTION_ABOUT);
  }, [isOpen, mode, selectedMissionTemplateKey]);

  const tabs = useMemo(() => ([
    {
      key: MISSION_EDITOR_SECTION_ABOUT,
      label: 'About',
      onClick: () => setSelectedTab(MISSION_EDITOR_SECTION_ABOUT),
    },
    {
      key: MISSION_EDITOR_SECTION_TASKS,
      label: 'Tasks',
      onClick: () => setSelectedTab(MISSION_EDITOR_SECTION_TASKS),
    },
  ]), []);

  const handleSave = async () => {
    const didSave = await onSaveMissionMeta?.();
    if (didSave === false) return;
    setOpen?.(false);
  };

  const handleDelete = async () => {
    if (!selectedMissionTemplateKey) return;
    const didDelete = await onDeleteMissionTemplate?.(selectedMissionTemplateKey);
    if (didDelete === false) return;
    setOpen?.(false);
  };

  return (
    <Modal
      iconKey={missionIconKey || 'account_tree'}
      title={mode === 'edit' ? 'Edit Playbook' : 'New Playbook'}
      isOpen={isOpen}
      width={isMobile ? '100%' : 540}
      maxHeight={820}
      onClose={() => setOpen?.(false)}
    >
      <div className="mission-modal-container">
        <Tabs
          selectedKey={selectedTab}
          tabs={tabs}
        />

        <MissionEditorForm
          missionDescription={missionDescription}
          missionIconKey={missionIconKey}
          missionMetaError={missionMetaError}
          missionTasks={missionTasks}
          missionTitle={missionTitle}
          section={selectedTab}
          onAddMissionTask={onAddMissionTask}
          onEditMissionTask={onEditMissionTask}
          onMissionDescriptionChange={onMissionDescriptionChange}
          onMissionIconKeyChange={onMissionIconKeyChange}
          onMissionTitleChange={onMissionTitleChange}
          onReorderMissionTask={onReorderMissionTask}
        />

        {canDesign && selectedTab === MISSION_EDITOR_SECTION_ABOUT ? (
          <div className="mission-modal-design-action">
            <Button
              enabled={!isSaving}
              iconKey="design_services"
              label="Design playbook"
              onClick={() => onDesignWorkflow?.()}
            />
          </div>
        ) : null}
      </div>

      <ModalButtons>
        <ModalButtonSection align="START">
          {mode === 'edit' ? (
            <Button
              enabled={!isSaving}
              iconKey="delete"
              label="Delete"
              onClick={handleDelete}
            />
          ) : null}
        </ModalButtonSection>

        <ModalButtonSection align="END">
          <Button
            enabled={!isSaving}
            iconKey="close"
            label="Cancel"
            onClick={() => setOpen?.(false)}
          />
          <Button
            enabled={canSaveMissionMeta && !isSaving}
            iconKey="check"
            label={isSaving ? 'Saving...' : 'Save'}
            onClick={handleSave}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

MissionModal.displayName = 'MissionModal';

export default MissionModal;
